/**
 * Client-side ShortlistItem model with custom methods and computed properties
 */

import type { Cost } from '@elara/domain/models/schemas/shared-schemas';
import type { RoomShortlistMetadata, ShortlistItemType } from '@elara/domain/models/schemas/shortlist-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';
import type { Destination } from './DestinationModel.js';
import type { FlightOptionModel } from './FlightOptionModel.js';
import type { Hotel } from './HotelModel.js';
import type { Place } from './PlaceModel.js';
import type { RoomOptionModel } from './RoomOptionModel.js';

import { ShortlistItemModelSpec } from '@elara/domain/models/ShortlistItemModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { hydratedFromHandle, parseDocumentId, tripIdFromPath } from '#lib/utils.js';

import { createClientDestinationModel } from './DestinationModel.js';
import { createClientFlightOptionModel } from './FlightOptionModel.js';
import { createClientHotelModel } from './HotelModel.js';
import { createClientPlaceModel } from './PlaceModel.js';
import { createClientRoomOptionModel } from './RoomOptionModel.js';

/**
 * ShortlistItem model for client-side usage
 * Provides reactive access to shortlist item data with computed properties
 */
export function createClientShortlistItemModel(firestore: FirestoreContainer) {
  // Local model accessors for related entities. These are created once per
  // ShortlistItem model and reused for all instances.
  const HotelModel = createClientHotelModel(firestore);
  const DestinationModel = createClientDestinationModel(firestore);
  const FlightOptionModel = createClientFlightOptionModel(firestore);
  const RoomOptionModel = createClientRoomOptionModel(firestore);
  const PlaceModel = createClientPlaceModel(firestore);

  const model = createClientModel(firestore, ShortlistItemModelSpec, () => ({
    /** Display title from persisted title or type label. Server resolves and persists title on add/update. */
    get displayTitle(): string {
      if (this.title != null && this.title.trim() !== '') return this.title;
      return this.typeLabel;
    },

    /** Formatted cost for display */
    get formattedCost(): string {
      if (!this.cost) return '';

      const { amount, currency = 'USD' } = this.cost;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    },

    /** Check if item has a message reference */
    get hasMessageRef(): boolean {
      return !!this.messageRef?.chatId && !!this.messageRef?.messageId;
    },

    /** Check if item is in a group */
    get isGrouped(): boolean {
      return !!this.groupId;
    },

    /** Check if item is a hotel */
    get isHotel(): boolean {
      return this.type === 'hotel';
    },

    /** Check if item is a room */
    get isRoom(): boolean {
      return this.type === 'room';
    },

    /** Check if item is a flight */
    get isFlight(): boolean {
      return this.type === 'flight';
    },

    /** Check if item is a destination */
    get isDestination(): boolean {
      return this.type === 'destination';
    },

    /** Check if item is an activity */
    get isActivity(): boolean {
      return this.type === 'activity';
    },

    /** Check if item is a restaurant */
    get isRestaurant(): boolean {
      return this.type === 'restaurant';
    },

    /** Check if item is an experience */
    get isExperience(): boolean {
      return this.type === 'experience';
    },

    /** Check if item is a place */
    get isPlace(): boolean {
      return this.type === 'place';
    },

    get startDateTime(): string | undefined {
      if (this.type === 'flight' && this.flightOption) {
        return this.flightOption.firstFlight('outbound')?.departure;
      }
      return undefined;
    },

    get endDateTime(): string | undefined {
      if (this.type === 'flight' && this.flightOption) {
        return this.flightOption.lastFlight('outbound')?.arrival;
      }
      return undefined;
    },

    /** Get icon name based on item type */
    get typeIcon(): IconName {
      const iconMap: Record<ShortlistItemType, IconName> = {
        hotel: 'hotel',
        room: 'hotel',
        flight: 'flight',
        destination: 'map',
        activity: 'local_activity',
        restaurant: 'restaurant',
        experience: 'attractions',
        place: 'place',
      };
      return iconMap[this.type] || 'bookmark';
    },

    /** Get label based on item type */
    get typeLabel(): string {
      const labelMap: Record<ShortlistItemType, string> = {
        hotel: 'Hotel',
        room: 'Room',
        flight: 'Flight',
        destination: 'Destination',
        activity: 'Activity',
        restaurant: 'Restaurant',
        experience: 'Experience',
        place: 'Place',
      };
      return labelMap[this.type] || 'Item';
    },

    /** Check if item has valid location data */
    get hasLocation(): boolean {
      return Boolean(this.mapPoint);
    },

    /**
     * Lazily hydrate the related room option from Firestore (type === 'room').
     * referenceId is the RoomOption document id. Returns undefined until ready or on error.
     */
    get roomOption(): RoomOptionModel | undefined {
      if (this.type !== 'room') return undefined;
      const tripId = tripIdFromPath(this.path);
      const refId = this.referenceId;
      if (!tripId || !refId) return undefined;
      const handle = RoomOptionModel.get({ tripId, id: refId });
      if (handle.isReady && handle.data) return handle.data;
      if (handle.error) {
        console.warn(`[ShortlistItem] Failed to load room option ${refId}:`, handle.error);
      }
      return undefined;
    },

    /**
     * Lazily hydrate the related hotel.
     * For hotel items: uses referenceId. For room items: uses roomOption.hotelId or referenceData.hotelId.
     * Returns undefined until the underlying document handle is ready.
     */
    get hotel(): Hotel | undefined {
      const hotelId =
        this.type === 'hotel'
          ? parseDocumentId(this.referenceId)
          : this.type === 'room'
            ? this.roomOption?.hotelId
            : undefined;
      if (!hotelId) return undefined;
      const handle = HotelModel.get({ id: hotelId });
      if (handle.error) {
        console.warn(`[ShortlistItem] Failed to load hotel ${hotelId}:`, handle.error);
        return undefined;
      }
      return hydratedFromHandle(handle) as Hotel | undefined;
    },

    /**
     * Lazily hydrate the related destination based on referenceId.
     * Returns undefined until the underlying document handle is ready.
     */
    get destination(): Destination | undefined {
      if (this.type !== 'destination') return undefined;
      const id = parseDocumentId(this.referenceId);
      if (!id) return undefined;
      const handle = DestinationModel.get({ id });
      if (handle.error) {
        console.warn(`[ShortlistItem] Failed to load destination ${id}:`, handle.error);
        return undefined;
      }
      return hydratedFromHandle(handle) as Destination | undefined;
    },

    /**
     * Lazily hydrate the related flight option from Firestore by referenceId.
     * Returns undefined until the document is ready or on error.
     */
    get flightOption(): FlightOptionModel | undefined {
      if (this.type !== 'flight') return undefined;
      const tripId = tripIdFromPath(this.path);
      const refId = this.referenceId;
      if (!tripId || !refId) return undefined;
      const handle = FlightOptionModel.get({ tripId, id: refId });
      if (handle.isReady && handle.data) return handle.data;
      if (handle.error) {
        console.warn(`[ShortlistItem] Failed to load flight option ${refId}:`, handle.error);
      }
      return undefined;
    },

    /**
     * Lazily hydrate the related place from Firestore by referenceId (place document id).
     * Returns undefined until the document is ready or on error.
     */
    get place(): Place | undefined {
      if (this.type !== 'place') return undefined;
      const placeId = parseDocumentId(this.referenceId);
      if (!placeId) return undefined;
      const handle = PlaceModel.get({ id: placeId });
      if (handle.error) {
        console.warn(`[ShortlistItem] Failed to load place ${placeId}:`, handle.error);
        return undefined;
      }
      return hydratedFromHandle(handle) as Place | undefined;
    },

    get cost(): Cost | undefined {
      if (this.type === 'flight') return this.flightOption?.cost;
      if (this.type === 'room') {
        const roomOption = this.roomOption;
        if (!roomOption) return undefined;
        // The same rate the card shows: a pinned rate when the item has one, otherwise the room's lead
        // rate for the saved party, falling back to the cheapest only once every rate is gone (the card's
        // struck-through price).
        const meta = this.metadata as Partial<RoomShortlistMetadata> | undefined;
        const rate = meta?.productCode
          ? roomOption.getRateByIdentity({ productCode: meta.productCode, occupancy: meta.occupancy })
          : (roomOption.leadRateFor(meta?.occupancy) ?? roomOption.cheapestRate);
        if (!rate) return undefined;
        return { amount: rate.localPricing.totalPrice * (meta?.quantity ?? 1), currency: rate.localPricing.currency };
      }
      return undefined;
    },

    get mapPoint(): MapPoint | undefined {
      // Hotel
      if (this.type === 'hotel' && this.hotel) {
        return this.hotel.mapPoint;
      }

      // Room (hotel from roomOption.hotelId or referenceData)
      if (this.type === 'room' && this.hotel) {
        return this.hotel.mapPoint;
      }

      // Destination
      if (this.type === 'destination' && this.destination) {
        return this.destination.mapPoint;
      }

      // Place
      if (this.type === 'place' && this.place) {
        return this.place.mapPoint;
      }

      return undefined;
    },
  }));

  return model;
}

export type ClientShortlistItemModel = ReturnType<typeof createClientShortlistItemModel>;
export type ShortlistItem = ClientInstanceOf<ClientShortlistItemModel>;
